import { Button, HStack, Stack, Text } from "@chakra-ui/react";
import { CheckIcon } from "@chakra-ui/icons";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Tooltip } from "../../../../../../common/components/Tooltip";
import { AiSuggestionTag } from "../../../../../../common/agent/AiSuggestionTag";
import { describeFieldSource } from "../../../../../../common/agent/fieldSourceDescription";
import { useAwaitingFieldSources } from "./FieldSourcesProvider";

interface Props {
  paths: ReadonlyArray<string> | undefined;
  fieldLabel: string;
  exact?: boolean;
}

export const FieldSourceMark = ({ paths, fieldLabel, exact = false }: Props) => {
  const { t } = useTranslation();
  const { awaiting, canReview, confirm } = useAwaitingFieldSources(paths, exact);
  const [isConfirming, setConfirming] = useState(false);
  if (awaiting.length === 0) return null;

  const descriptions = [
    ...new Set(
      awaiting.map(
        ({ source }) =>
          describeFieldSource(t, source, (details) =>
            typeof details.libraryElementTitle === "string" ? details.libraryElementTitle : null,
          ) ?? t("The assistant's suggestion"),
      ),
    ),
  ];

  return (
    <HStack as="span" spacing="1" ml="2" textTransform="none" letterSpacing="normal" fontWeight="normal">
      <Tooltip
        hasArrow
        label={
          <Stack spacing="0.5">
            {descriptions.map((description) => (
              <Text key={description} fontSize="sm">
                {t("Source")}: {description}
              </Text>
            ))}
          </Stack>
        }
      >
        <AiSuggestionTag label={t("Filled in by AI")} tabIndex={0} cursor="default" />
      </Tooltip>
      {canReview && (
        <Button
          size="xs"
          variant="ghost"
          colorScheme="purple"
          leftIcon={<CheckIcon boxSize="2.5" />}
          aria-label={t("Confirm {{field}} as the AI filled it in", { field: fieldLabel }) ?? ""}
          isLoading={isConfirming}
          onClick={async () => {
            setConfirming(true);
            await confirm();
            setConfirming(false);
          }}
        >
          {t("Confirm")}
        </Button>
      )}
    </HStack>
  );
};
